/* ===========================================================================
   haiku-modes — mode resolution, persistence, toggle behaviour.

   The FIRST-PAINT decision does not live here: a tiny blocking script in
   snippets/haiku-modes.liquid has already read storage, resolved auto
   against prefers-color-scheme and stamped data-mode on <html> before any
   pixel was drawn. This deferred file owns everything after that:

   - keeps `auto` live (matchMedia change → re-resolve, no reload)
   - persists explicit choices (localStorage; explicit beats auto)
   - syncs across tabs (storage event)
   - keeps <meta name="theme-color"> in step with the resolved mode
   - mounts the toggle (template#haiku-modes-toggle) into the footer's info
     column, or floats it when the footer is suppressed (story template)
   - full radiogroup keyboard behaviour (arrows / Home / End, roving tabindex)
   - announces changes through the theme's #screen-reader-info live region
   - survives theme-editor section re-renders (shopify:section:load)

   No dependencies. Config in window.haikuModesConfig (see the snippet).
   Public API: window.HaikuModes.{setting, resolved, set(mode)}.
   ========================================================================= */

(function () {
  'use strict';

  var d = document;
  var root = d.documentElement;
  var cfg = window.haikuModesConfig || {};

  if (cfg.enabled === false) { return; }

  var KEY = cfg.storageKey || 'haiku:mode';
  var MODES = ['light', 'dark', 'auto'];
  var DEFAULT_MODE = MODES.indexOf(cfg.defaultMode) > -1 ? cfg.defaultMode : 'auto';

  var media = null;
  try {
    media = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)');
  } catch (e) { /* ancient engine: auto degrades to light */ }

  /* ---- state ------------------------------------------------------------ */

  function readSetting() {
    var v = null;
    try { v = window.localStorage.getItem(KEY); } catch (e) { /* blocked */ }
    return MODES.indexOf(v) > -1 ? v : DEFAULT_MODE;
  }

  function writeSetting(mode) {
    try { window.localStorage.setItem(KEY, mode); } catch (e) { /* blocked */ }
  }

  function resolve(setting) {
    if (setting === 'light' || setting === 'dark') { return setting; }
    return (media && media.matches) ? 'dark' : 'light';
  }

  /* ---- application ------------------------------------------------------ */

  function syncThemeColorMeta(resolved) {
    var content = resolved === 'dark'
      ? (cfg.darkThemeColor || '#0a0a0a')
      : (cfg.lightThemeColor || '#ffffff');
    var meta = d.querySelector('meta[name="theme-color"]');
    if (!meta) {
      meta = d.createElement('meta');
      meta.setAttribute('name', 'theme-color');
      d.head.appendChild(meta);
    }
    meta.setAttribute('content', content);
  }

  function announce(setting) {
    var region = d.getElementById('screen-reader-info');
    if (!region) { return; }
    var labels = cfg.labels || {};
    region.textContent = (cfg.announcePrefix || 'Appearance:') + ' ' + (labels[setting] || setting);
  }

  /* The switch is intentionally instant — no colour crossfade. */
  function apply(setting, opts) {
    opts = opts || {};
    var resolved = resolve(setting);
    root.setAttribute('data-mode', resolved);
    root.setAttribute('data-mode-setting', setting);
    syncThemeColorMeta(resolved);
    syncControl(setting);
    if (opts.announce) { announce(setting); }
    if (opts.persist) { writeSetting(setting); }
  }

  /* ---- live `auto` -------------------------------------------------------- */

  function onMediaChange() {
    if (readSetting() === 'auto') { apply('auto'); }
  }

  if (media) {
    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', onMediaChange);
    } else if (typeof media.addListener === 'function') {
      media.addListener(onMediaChange); /* older Safari */
    }
  }

  /* Another tab changed the preference — follow it. */
  window.addEventListener('storage', function (e) {
    if (e.key === KEY) { apply(readSetting()); }
  });

  /* Restored from bfcache: the OS theme may have changed while away. */
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) { apply(readSetting()); }
  });

  /* ---- the toggle --------------------------------------------------------- */

  var control = null;

  function options() {
    return control
      ? Array.prototype.slice.call(control.querySelectorAll('[data-mode-value]'))
      : [];
  }

  function syncControl(setting) {
    options().forEach(function (btn) {
      var on = btn.getAttribute('data-mode-value') === setting;
      btn.setAttribute('aria-checked', on ? 'true' : 'false');
      btn.setAttribute('tabindex', on ? '0' : '-1'); /* roving tabindex */
    });
  }

  function onKeydown(e) {
    var key = e.key;
    var opts = options();
    var i = opts.indexOf(d.activeElement);
    if (i < 0) { return; }
    var next = -1;
    if (key === 'ArrowRight' || key === 'ArrowDown') { next = (i + 1) % opts.length; }
    else if (key === 'ArrowLeft' || key === 'ArrowUp') { next = (i - 1 + opts.length) % opts.length; }
    else if (key === 'Home') { next = 0; }
    else if (key === 'End') { next = opts.length - 1; }
    if (next < 0) { return; }
    e.preventDefault();
    /* Radio semantics: moving focus moves selection. */
    apply(opts[next].getAttribute('data-mode-value'), { persist: true, announce: true });
    opts[next].focus();
  }

  function onClick(e) {
    var btn = e.target && e.target.closest ? e.target.closest('[data-mode-value]') : null;
    if (!btn) { return; }
    apply(btn.getAttribute('data-mode-value'), { persist: true, announce: true });
  }

  function mount() {
    var tpl = d.getElementById('haiku-modes-toggle');
    if (!tpl || !('content' in tpl) || !tpl.content.firstElementChild) { return; }

    /* Theme-editor re-renders can orphan a previous mount. */
    var stray = d.querySelector('[data-haiku-modes]');
    if (stray) {
      if (d.contains(stray) && stray.offsetParent !== null) { control = stray; syncControl(readSetting()); return; }
      if (stray.parentNode) { stray.parentNode.removeChild(stray); }
    }

    control = tpl.content.firstElementChild.cloneNode(true);

    var target = null;
    if (cfg.mountSelector) {
      target = d.querySelector(cfg.mountSelector);
      /* offsetParent is null while hidden — e.g. the story template's
         suppressed footer. Fall back to the floating placement. */
      if (target && target.offsetParent === null) { target = null; }
    }

    if (target) {
      target.insertBefore(control, target.firstChild);
    } else if (cfg.floatingFallback !== false) {
      control.classList.add('haiku-modes--floating');
      d.body.appendChild(control);
    } else {
      control = null;
      return;
    }

    control.addEventListener('click', onClick);
    control.addEventListener('keydown', onKeydown);
    syncControl(readSetting());
  }

  function boot() {
    /* Re-assert: the bootstrap ran pre-paint, but this makes the deferred
       layer self-sufficient if the inline script was stripped by a
       minifier/app and keeps the meta + control in step. */
    apply(readSetting());
    mount();
  }

  if (d.readyState === 'loading') {
    d.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Theme editor: footer section re-rendered → remount. */
  d.addEventListener('shopify:section:load', function () {
    control = null;
    mount();
  });

  /* ---- public api --------------------------------------------------------- */

  window.HaikuModes = {
    get setting() { return readSetting(); },
    get resolved() { return resolve(readSetting()); },
    set: function (mode) {
      if (MODES.indexOf(mode) < 0) { return; }
      apply(mode, { persist: true });
    }
  };
}());
